import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Icon } from "@/lib/icon";
import { SERVICES, WAREHOUSE_IMAGE } from "@/lib/site-data";

const BUILT_TO_SUIT = {
    title: "Built-to-Suit Warehousing",
    body: "Dedicated warehouse development for long-term anchor customers — from 10,000 sqft to 120,000 sqft.",
    options: ["10,000 sqft", "20,000 sqft", "30,000 sqft", "60,000+ sqft on RFP"],
};

export default function Services() {
    return (
        <div data-testid="page-services">
            <PageHeader
                overline="Services"
                title="Integrated logistics services — every link of your supply chain."
                subtitle="From dedicated transportation to fully orchestrated 4PL programmes, choose the level of partnership your operations need."
                image={WAREHOUSE_IMAGE}
                breadcrumb={[{ label: "Services" }]}
                testId="services-header"
            />

            <section className="bg-white py-20 md:py-24">
                <div className="container-vl space-y-16">
                    {SERVICES.map((s, idx) => (
                        <div
                            key={s.slug}
                            id={s.slug}
                            data-testid={`service-block-${s.slug}`}
                            className={`grid grid-cols-12 gap-8 items-start ${idx % 2 === 1 ? "md:flex-row-reverse" : ""}`}
                        >
                            <div className="col-span-12 md:col-span-5">
                                <div className="bg-[#E9EEF3] p-8 md:p-10 border-l-4 border-[#F4A000]">
                                    <div className="w-14 h-14 bg-[#28406A] text-white inline-flex items-center justify-center mb-6">
                                        <Icon name={s.icon} className="h-7 w-7" strokeWidth={1.5} />
                                    </div>
                                    <p className="overline mb-2">0{idx + 1} · Service</p>
                                    <h2 className="heading-h3 text-[#101A2C] mb-3">{s.title}</h2>
                                    <p className="text-sm text-[#4B6390] leading-relaxed">{s.teaser}</p>
                                </div>
                            </div>
                            <div className="col-span-12 md:col-span-7">
                                <h3 className="overline mb-4 text-[#28406A]">What's included</h3>
                                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                                    {s.bullets.map((b) => (
                                        <li key={b} className="flex items-start gap-3 text-sm md:text-base text-[#101A2C]">
                                            <CheckCircle2 className="h-5 w-5 text-[#F4A000] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to="/contact"
                                    data-testid={`service-cta-${s.slug}`}
                                    className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-[#28406A] border-b-2 border-[#F4A000] pb-1"
                                >
                                    Request {s.title} Quote <ArrowRight className="h-3 w-3" />
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Built-to-suit warehousing */}
            <section className="bg-[#101A2C] text-white py-20 md:py-24 grain-overlay" data-testid="bts-section">
                <div className="container-vl grid grid-cols-12 gap-10 items-center">
                    <div className="col-span-12 lg:col-span-7">
                        <p className="overline mb-4">Built-to-Suit Warehousing</p>
                        <h2 className="heading-h2 text-white mb-5">Dedicated warehousing built around your operations.</h2>
                        <p className="text-[#CBD5E1] leading-relaxed mb-8 max-w-2xl">
                            {BUILT_TO_SUIT.body} Long-term anchor customers gain dedicated buildings, WMS integration, custom dock-doors and on-site embedded operations teams.
                        </p>
                        <Link to="/contact" data-testid="bts-cta" className="btn-primary">
                            Discuss Your Requirement <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                    <div className="col-span-12 lg:col-span-5">
                        <div className="bg-[#28406A] border border-[#F4A000]/40 p-8">
                            <p className="font-display uppercase text-sm text-[#F4A000] tracking-widest mb-5">Modular Configurations</p>
                            <ul className="space-y-3">
                                {BUILT_TO_SUIT.options.map((o) => (
                                    <li key={o} className="flex items-center justify-between border-b border-[#4B6390] pb-3">
                                        <span className="font-display uppercase text-lg">{o}</span>
                                        <span className="text-xs text-[#F4A000]">Available →</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="text-xs text-[#CBD5E1] mt-5">Up to 120,000 sqft development capability.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-[#F4A000] py-16" data-testid="services-cta">
                <div className="container-vl flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <h3 className="heading-h3 text-[#101A2C] max-w-2xl">Not sure which service fits? Start with a logistics assessment.</h3>
                    <Link to="/contact" data-testid="services-final-cta" className="btn-navy">
                        Schedule Consultation
                    </Link>
                </div>
            </section>
        </div>
    );
}
